import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select,
  Stack, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
} from '@mui/icons-material';
import menuFormatService from '../../services/menuFormatService';
import type { MenuFormat, MenuFormatKind } from '../../types/menu';

interface Props {
  venueId: string;
  menuId: string;
}

interface FormState {
  name: string;
  formatKind: MenuFormatKind;
  volumeMl: string;
  position: string;
}

const FORMAT_KINDS: MenuFormatKind[] = ['VOLUME', 'COURSE', 'UNIT', 'PORTION'];

const FORMAT_KIND_DESCRIPTIONS: Record<MenuFormatKind, string> = {
  VOLUME: 'Volume-based pour (ml). Glass, half glass, coravin, bottle.',
  COURSE: 'A course within a tasting menu. No volume.',
  UNIT: 'A discrete retail unit. No volume.',
  PORTION: 'A food serving size (small / large plate). No volume.',
};

const emptyForm: FormState = { name: '', formatKind: 'VOLUME', volumeMl: '', position: '' };

/**
 * Reusable serving-size catalog for a menu.
 *
 * Phase 4.M — operator-facing language change: "Format" → "Serving size".
 * The backend type names (MenuFormat, formatKind, formatId) and the schema
 * are intentionally NOT renamed; only the UI copy is. Each row describes
 * how an item can be served — VOLUME (with ml) for pours, or COURSE /
 * UNIT / PORTION when volume is meaningless. Selling prices are set
 * per item, not here.
 */
const MenuFormatsPanel: React.FC<Props> = ({ venueId, menuId }) => {
  const theme = useTheme();
  const [formats, setFormats] = useState<MenuFormat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MenuFormat | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    setLoading(true);
    setError('');
    menuFormatService.getFormats(venueId, menuId)
      .then(setFormats)
      .catch(() => setError('Could not load serving sizes. Please try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [venueId, menuId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, position: String(formats.length) });
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (f: MenuFormat) => {
    setEditing(f);
    setForm({
      name: f.name,
      formatKind: f.formatKind,
      volumeMl: f.volumeMl !== undefined && f.volumeMl !== null ? String(f.volumeMl) : '',
      position: String(f.position ?? 0),
    });
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError('Name is required.');
      return;
    }
    const positionNum = form.position === '' ? 0 : Number(form.position);
    if (!isFinite(positionNum)) {
      setFormError('Position must be a number.');
      return;
    }

    let volumeMlValue: number | null = null;
    if (form.formatKind === 'VOLUME') {
      const volumeNum = Number(form.volumeMl);
      if (!isFinite(volumeNum) || volumeNum < 1) {
        setFormError('Volume (ml) must be a positive number for VOLUME serving sizes.');
        return;
      }
      volumeMlValue = volumeNum;
    } else if (form.volumeMl.trim() !== '') {
      setFormError('Volume (ml) is only allowed when the serving size type is VOLUME.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      if (editing) {
        await menuFormatService.updateFormat(venueId, menuId, editing._id, {
          name: form.name.trim(),
          formatKind: form.formatKind,
          volumeMl: volumeMlValue,
          position: positionNum,
        });
      } else {
        await menuFormatService.createFormat(venueId, menuId, {
          name: form.name.trim(),
          formatKind: form.formatKind,
          volumeMl: volumeMlValue ?? undefined,
          position: positionNum,
        });
      }
      setDialogOpen(false);
      refresh();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'FORMAT_NAME_DUPLICATE') {
        setFormError('A serving size with this name already exists on this menu.');
      } else if (code === 'VOLUME_REQUIRED') {
        setFormError('Volume (ml) is required when the serving size type is VOLUME.');
      } else if (code === 'VOLUME_NOT_ALLOWED') {
        setFormError('Volume (ml) is only allowed when the serving size type is VOLUME.');
      } else {
        setFormError(err?.response?.data?.message ?? 'Could not save the serving size. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (f: MenuFormat) => {
    if (!window.confirm(`Delete serving size "${f.name}"? Existing prices for this serving size will stop showing on the customer view but are not erased.`)) return;
    try {
      await menuFormatService.deleteFormat(venueId, menuId, f._id);
      refresh();
    } catch {
      setError('Could not delete the serving size. Please try again.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>Serving sizes</Typography>
          <Typography variant="caption" color="text.secondary">
            Reusable serving sizes for this menu (e.g. Glass 150 ml, Coravin 60 ml, Course, Small Plate). Selling prices are set on each item.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          New serving size
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 1, mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : formats.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500} gutterBottom>
            No serving sizes yet.
          </Typography>
          {/* Phase 4.N — explain operational reuse. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 420, mx: 'auto', mb: 1 }}>
            Serving sizes can be reused across multiple items. Without them, items will only show their base price on the customer view.
          </Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openCreate} sx={{ mt: 1, textTransform: 'none' }}>
            Create first serving size
          </Button>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {formats.map(f => (
            <Paper
              key={f._id}
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Box sx={{
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 1, backgroundColor: alpha(theme.palette.primary.main, 0.08),
                fontSize: '0.78rem', fontFamily: 'monospace', color: theme.palette.text.secondary,
              }}>
                {f.position}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" fontWeight={600} noWrap>{f.name}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: theme.palette.text.secondary }}>
                    {f.formatKind}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {f.formatKind === 'VOLUME' && f.volumeMl != null ? `${f.volumeMl} ml` : '—'}
                </Typography>
              </Box>
              <Tooltip title="Edit serving size">
                <IconButton size="small" onClick={() => openEdit(f)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete serving size">
                <IconButton size="small" color="error" onClick={() => handleDelete(f)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Edit serving size' : 'New serving size'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>{formError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              autoFocus
              fullWidth
              required
              placeholder="e.g. Glass, Coravin, Course, Small Plate"
            />
            <FormControl fullWidth required>
              <InputLabel>Serving size type</InputLabel>
              <Select
                value={form.formatKind}
                label="Serving size type"
                onChange={e => setForm(f => ({ ...f, formatKind: e.target.value as MenuFormatKind, volumeMl: e.target.value === 'VOLUME' ? f.volumeMl : '' }))}
              >
                {FORMAT_KINDS.map(k => (
                  <MenuItem key={k} value={k}>{k}</MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                {FORMAT_KIND_DESCRIPTIONS[form.formatKind]}
              </Typography>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Volume (ml)"
                value={form.volumeMl}
                onChange={e => setForm(f => ({ ...f, volumeMl: e.target.value }))}
                type="number"
                required={form.formatKind === 'VOLUME'}
                disabled={form.formatKind !== 'VOLUME'}
                sx={{ flex: 1 }}
                slotProps={{ htmlInput: { min: 1, step: 1 } }}
                helperText={form.formatKind === 'VOLUME' ? 'Required for VOLUME serving sizes' : 'Only used for VOLUME serving sizes'}
              />
              <TextField
                label="Position"
                value={form.position}
                onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                type="number"
                sx={{ width: 120 }}
                helperText="Lower = earlier"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, minWidth: 100 }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuFormatsPanel;
